// One dense row per site: who it is for, committed against achievable size and COD,
// the Manager's RAG with the computed tests, the specialists' status, and the blockers
// and next actions it surfaces. The dashboard shows every site the daily run works; a
// project's Overview shows its own row, so the two read identically, with the site page's
// extras (sitePage below) added. See docs/Y2026-Q3/design-dashboard-v2.md.
import { Fragment, type ReactNode, useState } from "react";
import { Link } from "react-router-dom";

import type {
  AttributeDef,
  AttributeValue,
  DashboardRow,
  ItemLinks,
  LinkedItem,
  OptionValue,
  RagVerdict,
  SpecialistChips,
} from "../api/hooks/useDashboard";
import { byReference, rowSpecialists } from "../api/hooks/useDashboard";
import {
  type AgentPromptSummary,
  useStatefulAgents,
} from "../api/hooks/useStatefulAgents";
import { type DueShow, SHOW_OVERDUE, SHOW_UPCOMING } from "./NextActionTable";
import { RecordItem } from "./SummaryRefs";
import { recordLine } from "../utils/nextActions";
import {
  projectScheduledPath,
  projectSpecialistPath,
  projectStatusPath,
} from "../routes/learningAgentPaths";
import { conversationHref } from "../utils/reviewRunHref";
import {
  achievableKwpOptions,
  type InferredKwp,
  RATIO_BASIS_NAMES,
} from "../utils/achievableKwp";
import { sizeGap, type Tone } from "../utils/sizeGap";
import { adminSiteUrl } from "../utils/adminSite";
import {
  formatDay,
  formatOptionValue,
  isFigure,
  smallestFigure,
} from "../utils/attributeFormat";
import styles from "./DeploymentTable.module.css";
import { ContractStages } from "./ContractStages";
import { InfoToggle } from "./InfoToggle";
import {
  LifecycleChip,
  OwnerChip,
  VerdictChip,
  VerdictReason,
} from "./PortfolioRowParts";
import { bmaStatusLabel } from "../utils/bmaStatus";
import { useRowDrag } from "./useRowDrag";
import { Chevron } from "./Chevron";

const daysSince = (iso: string): number =>
  Math.max(
    0,
    Math.floor(
      (Date.now() - new Date(`${iso}T00:00:00`).getTime()) / 86_400_000,
    ),
  );

const TONE_CLASS: Record<Tone, string> = {
  amber: styles.below,
  red: styles.belowRed,
};

// COD: up to 8 weeks late is amber, later is red, as the RAG schedule test reads it.
const codGap = (
  achievable: number | string,
  base: number | string,
): Tone | undefined => {
  const days =
    (new Date(`${achievable}T00:00:00`).getTime() -
      new Date(`${base}T00:00:00`).getTime()) /
    86_400_000;
  return days > 56 ? "red" : days > 0 ? "amber" : undefined;
};

const figureTone = (
  v: OptionValue,
  tone: ((v: number | string) => Tone | undefined) | undefined,
): string | undefined => {
  const t = isFigure(v) ? tone?.(v) : undefined;
  return t ? TONE_CLASS[t] : undefined;
};

const PlatformSite = ({ row }: { row: DashboardRow }) => {
  const name = row.bma_site_name || "the admin site";
  return row.bma_site_id ? (
    <a
      href={adminSiteUrl(row.bma_site_id)}
      target="_blank"
      rel="noopener noreferrer"
    >
      {name}
    </a>
  ) : (
    <>{name}</>
  );
};

// What an achievable line's limits account for, merged across the figures on it.
const accountsFor = (values: (AttributeValue | undefined)[]): string => {
  const recorded = values.map((v) => v?.accounts_for ?? "").filter(Boolean);
  const named = [
    ...new Set(
      recorded.filter((t) => t !== NO_LIMITS).flatMap((t) => t.split(", ")),
    ),
  ];
  return named.length ? named.join(", ") : recorded.length ? NO_LIMITS : "";
};

// Mirrors project_attributes.NO_LIMITS.
const NO_LIMITS = "nothing yet";

// Whose limits an achievable line accounts for. "nothing yet" is red: an achievable figure
// no specialist has limited says nothing about whether committed is reachable, however
// well the two match.
const Accounts = ({ accounts }: { accounts: string }) => (
  <>
    accounts for:{" "}
    {accounts === NO_LIMITS ? (
      <span className={styles.belowRed}>{accounts}</span>
    ) : (
      accounts
    )}
  </>
);

// The attribute holding each party's asset manager; an owner-occupier's is recorded as
// the tenant's.
const PARTY_ASSET_MANAGER: Record<string, string> = {
  Tenant: "tenant_asset_manager",
  Landlord: "landlord_asset_manager",
  "Owner-occupier": "tenant_asset_manager",
};

const PARTY_PREFIX: Record<string, string> = {
  Tenant: "T",
  Landlord: "L",
  "Owner-occupier": "OO",
};

// A tenant or landlord with no name on record still shows an asset manager Legal found
// for it, on a line of its own.
const unnamedParties = (row: DashboardRow): [string, string][] => {
  const roles = new Set(row.parties.map((p) => p.role));
  if (roles.has("Owner-occupier")) return [];
  return (["Tenant", "Landlord"] as const)
    .filter((role) => !roles.has(role))
    .map((role) => [role, PARTY_ASSET_MANAGER[role]]);
};

// One line under a party: who manages the building for it, and why behind the (i).
// Nothing until Legal has recorded one.
const AssetManager = ({
  label,
  definition,
  value,
}: {
  label: string;
  definition: AttributeDef | undefined;
  value: AttributeValue | undefined;
}) =>
  definition && value ? (
    <span className={styles.small}>
      {label}:{" "}
      {value.options.map((o, i) => (
        <span key={i}>
          {i > 0 && " or "}
          {o.value !== null && typeof o.value === "object" ? (
            <>
              {o.value.name || o.value.org}
              {o.value.name && `, ${o.value.org}`}
            </>
          ) : (
            <span className={styles.muted}>
              {definition.no_figure}
              {o.label && ` (${o.label})`}
            </span>
          )}
        </span>
      ))}
      {value.reason && (
        <InfoToggle label={`Why ${definition.label}`}>
          {value.reason}
        </InfoToggle>
      )}
    </span>
  ) : null;

// One row of the grid: a quantity, the committed and achievable attributes of each of
// its parts (a battery's power then energy, on one row), and how far short of committed
// an achievable figure has to fall to be flagged.
type SizePart = { committed: string; achievable: string };
type SizeRow = {
  label: string;
  parts: SizePart[];
  gap: (achievable: number | string, base: number | string) => Tone | undefined;
};

const COD_ROW: SizeRow = {
  label: "COD",
  parts: [{ committed: "committed_cod", achievable: "achievable_cod" }],
  gap: codGap,
};

const SIZE_ROWS: SizeRow[] = [
  {
    label: "kWp",
    parts: [{ committed: "committed_kwp", achievable: "achievable_kwp" }],
    gap: sizeGap,
  },
  {
    label: "kWac",
    parts: [{ committed: "committed_kw_ac", achievable: "achievable_kw_ac" }],
    gap: sizeGap,
  },
  {
    label: "export kWac",
    parts: [
      { committed: "committed_export_kw", achievable: "achievable_export_kw" },
    ],
    gap: sizeGap,
  },
];

const BESS_KW: SizePart = {
  committed: "committed_bess_kw",
  achievable: "achievable_bess_kw",
};
const BESS_KWH: SizePart = {
  committed: "committed_bess_kwh",
  achievable: "achievable_bess_kwh",
};

// A site's own page shows the battery's power and energy; the dashboard its power only,
// so the row stays as narrow as the others.
const BATTERY_ROW: SizeRow = {
  label: "battery",
  parts: [BESS_KW, BESS_KWH],
  gap: sizeGap,
};
const BATTERY_POWER_ROW: SizeRow = {
  label: "battery kW",
  parts: [BESS_KW],
  gap: sizeGap,
};

// A figure without its unit where the row names it, or with it where a row carries two
// ("250 kW / 500 kWh").
const sizeFigure = (
  definition: AttributeDef,
  value: OptionValue,
  unit: boolean,
): string =>
  unit || !isFigure(value) || definition.kind === "date"
    ? formatOptionValue(definition, value)
    : (Number(value) * definition.display_scale).toLocaleString("en-GB", {
        minimumFractionDigits: definition.decimals,
        maximumFractionDigits: definition.decimals,
      });

const kw = (n: number): string =>
  n.toLocaleString("en-GB", { maximumFractionDigits: 0 });

// Why an italic kWp is there: the grid kWac it came from and the ratio applied, as the
// backend inferred it (achievable_kwp.describe_inferred says it the same way to agents).
const inferredNote = (
  definition: AttributeDef,
  value: OptionValue,
  from: InferredKwp,
): string =>
  `${formatOptionValue(definition, value)} is inferred from ${kw(from.kw_ac)} kWac, the ` +
  `grid's limit on its route, at the ${RATIO_BASIS_NAMES[from.basis]} DC/AC ratio ` +
  (from.dc_kwp !== null && from.ac_kw !== null
    ? `(${kw(from.dc_kwp)} kWp / ${kw(from.ac_kw)} kWac)`
    : from.ratio.toFixed(2));

// An attribute's options with a figure, as numbers.
const numbers = (options: { value: OptionValue }[] | undefined): number[] =>
  (options ?? []).flatMap((o) => (typeof o.value === "number" ? [o.value] : []));

// Whose limits an achievable line accounts for, merged across its figures; hovering it
// names each figure's own.
const AccountsLine = ({
  parts,
  row,
  definitions,
  labelOf,
  className,
}: {
  parts: SizePart[];
  row: DashboardRow;
  definitions: Map<string, AttributeDef>;
  labelOf: (p: SizePart, definition: AttributeDef) => string;
  className?: string;
}) => {
  const accounts = accountsFor(parts.map((p) => row.attributes[p.achievable]));
  if (!accounts) return null;
  const limits = parts.flatMap((p) => {
    const definition = definitions.get(p.achievable);
    const recorded = row.attributes[p.achievable]?.accounts_for;
    if (!definition || !recorded) return [];
    return [
      <span key={p.achievable} className={styles.infoLine}>
        <strong>{labelOf(p, definition)}</strong> {recorded}
      </span>,
    ];
  });
  return (
    <span className={`${styles.accounts} ${className ?? ""}`}>
      {limits.length > 0 ? (
        <InfoToggle
          label="What achievable accounts for"
          trigger={<Accounts accounts={accounts} />}
        >
          {limits}
          <span className={styles.infoLine}>
            Achievable is the most each figure can be within the limits the
            grid, structural and design specialists have recorded. One not named
            has set no limit on it yet.
          </span>
        </InfoToggle>
      ) : (
        <Accounts accounts={accounts} />
      )}
    </span>
  );
};

// Committed beside achievable, a row per quantity so each pair reads across: COD first,
// then the sizes. Each column heading's (i) says why that column's figures were set. A
// site's own page also shows the battery's energy and, under COD and under the sizes,
// whose limits their achievable figures account for, each opening on hover; the
// dashboard keeps only the merged "accounts for" at the start of Achievable's (i).
const CommittedAchievable = ({
  row,
  definitions,
  sitePage,
}: {
  row: DashboardRow;
  definitions: Map<string, AttributeDef>;
  sitePage: boolean;
}) => {
  const a = row.attributes;
  const hasBattery = BATTERY_ROW.parts.some(
    (p) => a[p.committed] || a[p.achievable] || row.pitched[p.committed],
  );
  const batteryRow = sitePage ? BATTERY_ROW : BATTERY_POWER_ROW;
  const sizeRows = hasBattery ? [...SIZE_ROWS, batteryRow] : SIZE_ROWS;
  const rows = [COD_ROW, ...sizeRows];
  const sizeParts = sizeRows.flatMap((r) => r.parts);
  const kwpOptions = achievableKwpOptions(row);
  // A side's options, achievable kWp with any inferred from AC.
  const optionsOf = (
    p: SizePart,
    side: "committed" | "achievable",
  ):
    | { value: OptionValue; label: string; inferred?: InferredKwp | null }[]
    | undefined =>
    side === "achievable" && p.achievable === "achievable_kwp"
      ? kwpOptions
      : a[p[side]]?.options;

  // One part's figures on its side: its options ("or ..."), toned on the achievable side
  // where they fall short of committed.
  const figures = (
    r: SizeRow,
    p: SizePart,
    side: "committed" | "achievable",
  ): ReactNode => {
    const definition = definitions.get(p[side]);
    const options = optionsOf(p, side);
    if (!definition || !options)
      return <span className={styles.muted}>not set</span>;
    const base =
      smallestFigure(a[p.committed]) ?? row.pitched[p.committed]?.value;
    const tone =
      side === "achievable"
        ? (v: number | string) =>
            base === undefined ? undefined : r.gap(v, base)
        : undefined;
    return (
      <span className={styles.sizeOptions}>
        {options.map((o, i) => (
          <span key={i} className={styles.value}>
            {i > 0 && <span className={styles.muted}>or </span>}
            <span
              className={[
                figureTone(o.value, tone),
                o.inferred && styles.inferred,
              ]
                .filter(Boolean)
                .join(" ")}
            >
              {sizeFigure(definition, o.value, r.parts.length > 1)}
            </span>
          </span>
        ))}
      </span>
    );
  };
  const cell = (r: SizeRow, side: "committed" | "achievable") => (
    <span className={styles.sizeNum}>
      {r.parts.map((p, i) => (
        <Fragment key={p[side]}>
          {i > 0 && <span className={styles.muted}>/</span>}
          {figures(r, p, side)}
        </Fragment>
      ))}
    </span>
  );

  // A figure the platform proposes that nothing committed matches, including one not
  // committed at all.
  const differing = sizeParts.filter((p) => {
    const definition = definitions.get(p.committed);
    const figure = row.pitched[p.committed];
    if (!definition || !figure) return false;
    const proposed = formatOptionValue(definition, figure.value);
    return !(a[p.committed]?.options ?? []).some(
      (o) => formatOptionValue(definition, o.value) === proposed,
    );
  });

  // A size whose every achievable figure is above what is committed: the limits allow
  // more than the contract, so it needs amending. Never against pitched, which binds no one.
  const aboveContract = sizeParts.flatMap((p) => {
    const committed = numbers(a[p.committed]?.options);
    const achievable = numbers(optionsOf(p, "achievable"));
    if (!committed.length || !achievable.length) return [];
    const most = Math.max(...committed);
    const least = Math.min(...achievable);
    return least > most ? [{ part: p, committed: most, achievable: least }] : [];
  });

  // A figure's row label, with its unit where the row carries two ("battery kW").
  const labelOf = (p: SizePart, definition: AttributeDef): string => {
    const r = rows.find((candidate) => candidate.parts.includes(p)) as SizeRow;
    return r.parts.length > 1
      ? `${r.label} ${definition.display_unit}`
      : r.label;
  };
  // Why each figure on one side was set, one line per figure after its row's label:
  // what each option depends on, then the reason.
  const info = (side: "committed" | "achievable"): ReactNode[] =>
    rows.flatMap((r) =>
      r.parts.flatMap((p) => {
        const definition = definitions.get(p[side]);
        const options = optionsOf(p, side) ?? [];
        const reason = a[p[side]]?.reason;
        if (!definition) return [];
        const why = [
          ...options
            .filter((o) => o.label)
            .map(
              (o) =>
                `${formatOptionValue(definition, o.value)}: ${o.label}`,
            ),
          ...(reason ? [reason] : []),
          ...options.flatMap((o) =>
            o.inferred ? [inferredNote(definition, o.value, o.inferred)] : [],
          ),
        ];
        if (!why.length) return [];
        return [
          <span key={p[side]} className={styles.infoLine}>
            <strong>{labelOf(p, definition)}</strong> {why.join(". ")}
          </span>,
        ];
      }),
    );
  const accounts = accountsFor(
    rows.flatMap((r) => r.parts).map((p) => a[p.achievable]),
  );
  // In the Achievable (i) only: the sizes the limits allow above what is committed.
  const aboveContractInfo: ReactNode[] = aboveContract.map(
    ({ part, committed, achievable }) => {
      const definition = definitions.get(part.achievable) as AttributeDef;
      return (
        <span key={`above-${part.achievable}`} className={styles.infoLine}>
          <strong>{labelOf(part, definition)}</strong> Above contract:{" "}
          {sizeFigure(definition, achievable, false)} achievable against{" "}
          {sizeFigure(definition, committed, false)} committed. Amendment needed,
          as one Legal action taken last.
        </span>
      );
    },
  );
  const heading = (side: "committed" | "achievable", text: string) => {
    const lines = [
      ...(side === "achievable" && !sitePage && accounts
        ? [
            <span key="accounts" className={styles.infoLine}>
              <Accounts accounts={accounts} />
            </span>,
          ]
        : []),
      ...(side === "achievable" ? aboveContractInfo : []),
      ...info(side),
    ];
    return (
      <span className={styles.sizeHead}>
        {text}
        {lines.length > 0 && (
          <InfoToggle label={`Why the ${side} sizes`}>{lines}</InfoToggle>
        )}
      </span>
    );
  };
  return (
    <span className={styles.sizeGrid}>
      <span />
      {heading("committed", "Committed")}
      {heading("achievable", "Achievable")}
      {rows.map((r) => (
        <Fragment key={r.label}>
          <span className={styles.sizeUnit}>{r.label}</span>
          {cell(r, "committed")}
          {cell(r, "achievable")}
          {sitePage && r === COD_ROW && (
            <AccountsLine
              parts={COD_ROW.parts}
              row={row}
              definitions={definitions}
              labelOf={labelOf}
              className={`${styles.underAchievable} ${styles.afterCod}`}
            />
          )}
        </Fragment>
      ))}
      {differing.length > 0 && (
        <span className={`${styles.differs} ${styles.underCommitted}`}>
          <InfoToggle
            label="The proposed size"
            trigger="different from proposed"
          >
            {differing.map((p) => {
              const definition = definitions.get(p.committed) as AttributeDef;
              return (
                <span key={p.committed} className={styles.infoLine}>
                  <strong>{labelOf(p, definition)}</strong> Committed:{" "}
                  {(a[p.committed]?.options ?? [])
                    .map((o) => sizeFigure(definition, o.value, false))
                    .join(" or ") || "not set"}{" "}
                  Proposed:{" "}
                  {sizeFigure(
                    definition,
                    row.pitched[p.committed].value,
                    false,
                  )}
                </span>
              );
            })}
            <span className={styles.infoLine}>
              Proposed on <PlatformSite row={row} />
              {row.bma_site_updated_on &&
                `, last updated ${formatDay(row.bma_site_updated_on)}`}
              . Refreshed daily. Update whichever is out of date.
            </span>
          </InfoToggle>
        </span>
      )}
      {sitePage && (
        <AccountsLine
          parts={sizeParts}
          row={row}
          definitions={definitions}
          labelOf={labelOf}
          className={styles.underAchievable}
        />
      )}
    </span>
  );
};

// What acts on a blocker or action: its decision or draft email, named with its
// project when it is filed on another (one message serving two sites), struck through
// once decided or sent.
const LINK_WORDS: Record<
  LinkedItem["state"],
  Record<LinkedItem["kind"], string>
> = {
  open: { decision: "decision", email: "email" },
  closed: { decision: "decision closed", email: "email" },
  sent: { decision: "decision", email: "email sent" },
  dropped: { decision: "decision", email: "email dropped" },
};

// A decision is seen in its specialist's Next actions; an email on the run that wrote it.
const linkedItemPath = (l: LinkedItem, rowProjectId: string, id: string): string => {
  const projectId = l.project_id ?? rowProjectId;
  if (l.kind === "decision") {
    return `${projectSpecialistPath(projectId, l.agent)}#${l.action_id || "next-actions"}`;
  }
  return `${projectScheduledPath(projectId, l.run_id)}#${id}`;
};

const Linked = ({ item, row }: { item: ItemLinks; row: DashboardRow }) => {
  const linked = [item.decision_id, item.email_id]
    .filter((id): id is string => !!id && row.links[id] !== undefined)
    .map((id) => [id, row.links[id]] as const);
  if (linked.length === 0) return null;
  return (
    <span className={styles.links}>
      {linked.map(([id, l]) => {
        const where =
          l.project_id && l.project_id !== row.project_id
            ? ` on ${l.project_name}`
            : "";
        return (
          <Link
            key={id}
            className={l.state === "open" ? styles.link : styles.doneLink}
            to={linkedItemPath(l, row.project_id, id)}
          >
            → {LINK_WORDS[l.state][l.kind]}
            {where}
          </Link>
        );
      })}
    </span>
  );
};

const RagChip = ({ verdict }: { verdict: RagVerdict }) => (
  <span className={`${styles.rag} ${styles[verdict]}`}>{verdict}</span>
);

// "next_gate" -> "Next gate".
const checkName = (key: string) => {
  const words = key.replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
};

// A specialist's verdict, stage and owner, linking to its page, and why the verdict, as
// its card on the Status page leads with them.
const SpecialistStatus = ({
  projectId,
  agent,
  label,
  chips,
}: {
  projectId: string;
  agent: string;
  label: string;
  chips: SpecialistChips | null | undefined;
}) => {
  if (!chips) return <span className={styles.muted}>no status</span>;
  return (
    <span className={styles.stack}>
      <Link
        className={styles.chips}
        to={projectSpecialistPath(projectId, agent)}
        title={`Open this site's ${label} page`}
      >
        <VerdictChip onTrack={chips.on_track} />
        <LifecycleChip lifecycle={chips.lifecycle} />
        <OwnerChip
          who={chips.next_move?.who ?? chips.next_step_owner.who}
          lifecycle={chips.lifecycle}
          overdueFrom={chips.next_move?.overdue_from}
          move={chips.next_move}
        />
      </Link>
      <span className={styles.statusReason}>
        <VerdictReason onTrack={chips.on_track} found={null} />
      </span>
    </span>
  );
};

// A list shown as its first item in full and the rest cut to one line each; a tap on a
// cut line opens every item in full. Without `onToggle` it always shows every item.
const Collapsible = ({
  texts,
  open,
  onToggle,
  children,
}: {
  texts: string[];
  open: boolean;
  onToggle?: () => void;
  children: ReactNode[];
}) => (
  <span className={styles.stack}>
    <ol className={styles.items}>
      {open
        ? children
        : [
            children[0],
            ...texts.slice(1).map((text, i) => (
              <li key={`cut${i}`}>
                <button
                  type="button"
                  className={styles.oneLine}
                  onClick={onToggle}
                  title={text}
                >
                  {text}
                </button>
              </li>
            )),
          ]}
    </ol>
    {onToggle && open && texts.length > 1 && (
      <button type="button" className={styles.more} onClick={onToggle}>
        show less
      </button>
    )}
  </span>
);

// The Manager's verdict's reasons: the Manager's view and any override, then each
// automatic check.
const RagReasons = ({ row }: { row: DashboardRow }) => {
  const recorded = row.summary?.summary.rag;
  const computed = row.rag.verdict;
  return (
    <>
      <div className={styles.ragSection}>
        <span className={styles.ragLabel}>
          Manager's view
          {recorded && <RagChip verdict={recorded.verdict} />}
        </span>
        <p>
          {recorded
            ? recorded.reason
            : "The Manager has not judged this site yet."}
        </p>
        {recorded?.override_reason && (
          <p className={styles.small}>
            <strong>Override:</strong> {recorded.override_reason}
          </p>
        )}
      </div>
      <div className={styles.ragSection}>
        <span className={styles.ragLabel}>
          Automatic checks
          <RagChip verdict={computed} />
        </span>
        <ul className={styles.ragChecks}>
          {Object.entries(row.rag.tests).map(([name, t]) => (
            <li key={name}>
              <span
                className={`${styles.ragDot} ${styles[t.verdict]}`}
                role="img"
                aria-label={t.verdict}
              />
              <span>
                <strong>{checkName(name)}</strong>: {t.detail}
              </span>
            </li>
          ))}
        </ul>
      </div>
      {row.rag.legacy && (
        <details className={styles.ragSection}>
          <summary className={styles.ragLabel}>
            Previous checks, kept to compare against
            <RagChip verdict={row.rag.legacy.verdict} />
          </summary>
          <ul className={styles.ragPrevious}>
            {Object.entries(row.rag.legacy.tests).map(([name, t]) => (
              <li key={name}>
                <strong>{checkName(name)}:</strong> {t.verdict} - {t.detail}
              </li>
            ))}
          </ul>
        </details>
      )}
    </>
  );
};

// Each agent's overdue or upcoming count, linking to those actions on its page.
const byAgent = (row: DashboardRow, show: DueShow) =>
  (show === SHOW_OVERDUE ? row.overdue_by_agent : row.upcoming_by_agent) ?? [];

const DueByAgent = ({ row, show }: { row: DashboardRow; show: DueShow }) => (
  <ul className={styles.overdueAgents}>
    {byAgent(row, show).map((a) => (
      <li key={a.agent}>
        <Link
          to={`${projectSpecialistPath(row.project_id, a.agent)}?show=${show}#next-actions`}
        >
          {a.label}
        </Link>{" "}
        {a.count} {show}
      </li>
    ))}
  </ul>
);

type HealthPanel = "rag" | DueShow;

// A head on the health line that opens its panel beneath the line on a click.
const HealthToggle = ({
  label,
  open,
  onToggle,
  children,
}: {
  label: string;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}) => (
  <button
    type="button"
    className={`${styles.expandToggle} ${styles.healthToggle}`}
    aria-expanded={open}
    aria-label={`${open ? "Hide" : "Show"} ${label}`}
    onClick={onToggle}
  >
    {children}
  </button>
);

// The site's health on one line: the Manager's verdict (the automatic checks' beside it
// as "Red or Amber" wherever they differ, and alone, marked "not judged", where the Manager
// has not judged), then its overdue (red) and upcoming (grey) actions, every specialist's,
// counted as the Overview counts them. Each opens why, or each agent's count, beneath.
const SiteHealth = ({
  row,
  feedback,
}: {
  row: DashboardRow;
  /** The feedback chip: not on the site's own page, whose tabs count it per specialist. */
  feedback: boolean;
}) => {
  const [open, setOpen] = useState<HealthPanel | null>(null);
  const toggle = (panel: HealthPanel) =>
    setOpen((was) => (was === panel ? null : panel));
  const recorded = row.summary?.summary.rag;
  const computed = row.rag.verdict;
  const differs = recorded !== undefined && recorded.verdict !== computed;
  const due = (show: DueShow) => {
    const total = show === SHOW_OVERDUE ? row.overdue : row.upcoming;
    if (!total) return null;
    const count =
      show === SHOW_OVERDUE ? (
        <span className={`${styles.rag} ${styles.dueOverdue}`}>
          {total} overdue
        </span>
      ) : (
        <span className={styles.dueUpcoming}>{total} upcoming</span>
      );
    return byAgent(row, show).length === 0 ? (
      count
    ) : (
      <HealthToggle
        label={`${show} actions by agent`}
        open={open === show}
        onToggle={() => toggle(show)}
      >
        {count}
      </HealthToggle>
    );
  };
  return (
    <div className={styles.stack}>
      <div className={styles.healthLine}>
        <HealthToggle
          label="why this RAG"
          open={open === "rag"}
          onToggle={() => toggle("rag")}
        >
          <RagChip verdict={recorded?.verdict ?? computed} />
          {!recorded ? (
            <span className={styles.muted}>not judged</span>
          ) : differs ? (
            <>
              <span className={styles.muted}>or</span>
              <RagChip verdict={computed} />
            </>
          ) : null}
        </HealthToggle>
        {due(SHOW_OVERDUE)}
        {due(SHOW_UPCOMING)}
      </div>
      {/* Agent questions, every specialist's together: a line of their own, apart from the
          actions above. The project page lists them by specialist. */}
      {feedback && !!row.feedback_open && (
        <Link
          className={`${styles.rag} ${styles.feedbackChip}`}
          to={projectStatusPath(row.project_id)}
        >
          {row.feedback_open} feedback
        </Link>
      )}
      {open === "rag" && (
        <div className={`${styles.expanded} ${styles.expandedWide}`}>
          <RagReasons row={row} />
        </div>
      )}
      {(open === SHOW_OVERDUE || open === SHOW_UPCOMING) && (
        <div className={styles.expanded}>
          <DueByAgent row={row} show={open} />
        </div>
      )}
    </div>
  );
};

// What waits on the operator, under the site's name: review comments needing an answer,
// each opening its conversation.
const ReviewWaiting = ({ row }: { row: DashboardRow }) => {
  const review = row.review_waiting;
  const reviewHrefs = (review?.conversation_ids ?? []).flatMap((id) => {
    const href = conversationHref(id, row.project_id);
    return href ? [href] : [];
  });
  const reviewLabel = (
    <>
      <span className={styles.countBadge}>{review?.count}</span>
      {review?.count === 1
        ? "review comment needs your input"
        : "review comments need your input"}
    </>
  );
  return (
    <>
      {review &&
        (reviewHrefs.length === 1 ? (
          <Link
            className={`${styles.small} ${styles.waiting}`}
            to={reviewHrefs[0]}
          >
            {reviewLabel}
          </Link>
        ) : (
          <span className={`${styles.small} ${styles.waitingMany}`}>
            <span className={styles.waitingLabel}>
              {reviewLabel}
              {reviewHrefs.length > 0 && ":"}
            </span>
            {reviewHrefs.length > 0 && (
              <ul className={styles.waitingLinks}>
                {reviewHrefs.map((href, i) => (
                  <li key={href}>
                    <Link to={href}>conversation {i + 1}</Link>
                  </li>
                ))}
              </ul>
            )}
          </span>
        ))}
    </>
  );
};

type Column = {
  header: string;
  className?: string;
  // Stays in view while the table scrolls sideways.
  frozen?: boolean;
  cell: (row: DashboardRow) => ReactNode;
  // One specialist's status.
  specialist?: boolean;
};

// On one site's own page the table wraps rather than scrolling sideways: the first line
// takes this many specialists, and the second starts at the next one, or at the blockers.
const SPECIALISTS_ON_FIRST_LINE = 3;

export const DeploymentTable = ({
  attributes,
  rows,
  onMove,
  moving,
  linkNames = true,
  split = false,
  rowAttrs,
  rowAction,
  afterRow,
  sitePage,
}: {
  attributes: AttributeDef[];
  rows: DashboardRow[];
  // Omitted: no reordering (a sort other than the planned order, or a single row).
  // `to` is the index the row lands on once taken out of the order.
  onMove?: (projectId: string, to: number) => void;
  moving?: boolean;
  linkNames?: boolean;
  // Lay each row out on two lines, Site to the first specialists above the rest.
  split?: boolean;
  // A site's own page: the contract stages under the specialist that keeps them, every
  // specialist working the site beside the dashboard's, each party's asset manager under its name, the battery's energy beside its
  // power, the "accounts for" lines, and every blocker and next action in full.
  // The dashboard drops them, and cuts the lists to one line after the first, to stay narrow.
  sitePage: boolean;
  /** Attributes for each row's cells, such as the review anchor of its project. */
  rowAttrs?: (row: DashboardRow) => Record<string, string>;
  /** A control shown under the site's name. */
  rowAction?: (row: DashboardRow) => ReactNode;
  /** A full-width row under the site's own, or null for none. */
  afterRow?: (row: DashboardRow) => ReactNode;
}) => {
  const definitions = new Map(attributes.map((a) => [a.key, a]));
  const index = new Map(rows.map((row, i) => [row.project_id, i]));
  // The dashboard's columns in their order, then on a site's own page every other
  // specialist its row carries, in team order (project_row_order in registry.py).
  const roster = useStatefulAgents().data?.agents ?? [];
  const columnRank = (a: AgentPromptSummary) => a.dashboard_column ?? Infinity;
  const specialists = roster
    .filter(
      (a) =>
        a.keeps_status_row &&
        (a.dashboard_column !== null ||
          (sitePage && rows.some((row) => a.agent in rowSpecialists(row)))),
    )
    .map((a, i) => ({ a, i }))
    .sort((x, y) => columnRank(x.a) - columnRank(y.a) || x.i - y.i)
    .map(({ a }) => a);
  // Rows whose blockers and next actions are open in full; both columns open together.
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggle = (projectId: string) =>
    setExpanded((was) => {
      const next = new Set(was);
      if (!next.delete(projectId)) next.add(projectId);
      return next;
    });
  const listState = (projectId: string) =>
    sitePage
      ? { open: true }
      : { open: expanded.has(projectId), onToggle: () => toggle(projectId) };
  const { draggingId, handleProps, rowStyle } = useRowDrag(
    rows.map((row) => row.project_id),
    (projectId, to) => onMove?.(projectId, to),
    !onMove || moving,
  );

  const columns: Column[] = [
    ...(onMove
      ? [
          {
            header: "#",
            className: styles.rankCell,
            frozen: true,
            cell: (row: DashboardRow) => {
              const i = index.get(row.project_id) ?? 0;
              return (
                <span className={styles.order}>
                  <button
                    type="button"
                    className={styles.move}
                    aria-label={`Move ${row.name} up`}
                    disabled={moving || i === 0}
                    onClick={() => onMove(row.project_id, i - 1)}
                  >
                    <Chevron open />
                  </button>
                  <span
                    className={styles.rank}
                    title="Drag to move this site"
                    {...handleProps(row.project_id)}
                  >
                    {i + 1}
                  </span>
                  <button
                    type="button"
                    className={styles.move}
                    aria-label={`Move ${row.name} down`}
                    disabled={moving || i === rows.length - 1}
                    onClick={() => onMove(row.project_id, i + 1)}
                  >
                    <Chevron />
                  </button>
                </span>
              );
            },
          },
        ]
      : []),
    {
      header: "Site",
      className: styles.site,
      frozen: true,
      cell: (row) => (
        <span className={styles.stack}>
          {linkNames ? (
            <Link
              className={styles.name}
              to={projectStatusPath(row.project_id)}
            >
              {row.name || row.project_id}
            </Link>
          ) : (
            <strong className={styles.name}>
              {row.name || row.project_id}
            </strong>
          )}
          {row.bma_status && (
            <span
              className={styles.bmaStatus}
              title="The site's status on admin.inrange.io"
            >
              {bmaStatusLabel(row.bma_status)}
            </span>
          )}
          <SiteHealth row={row} feedback={linkNames} />
          <ReviewWaiting row={row} />
          {rowAction?.(row)}
          {row.parties.map((party) => (
            <span key={party.role} className={styles.stack}>
              <span className={styles.small}>
                <span className={styles.value} title={party.role}>
                  {PARTY_PREFIX[party.role]}:
                </span>{" "}
                {party.name}
              </span>
              {sitePage && (
                <AssetManager
                  label="Asset manager"
                  definition={definitions.get(PARTY_ASSET_MANAGER[party.role])}
                  value={row.attributes[PARTY_ASSET_MANAGER[party.role]]}
                />
              )}
            </span>
          ))}
          {sitePage &&
            unnamedParties(row).map(([role, key]) => (
              <AssetManager
                key={key}
                label={`${role}'s asset manager`}
                definition={definitions.get(key)}
                value={row.attributes[key]}
              />
            ))}
        </span>
      ),
    },
    {
      header: "Committed vs. Achievable",
      className: styles.sizes,
      cell: (row) => (
        <CommittedAchievable
          row={row}
          definitions={definitions}
          sitePage={sitePage}
        />
      ),
    },
    ...specialists.map(
      (spec): Column => ({
        header: spec.label,
        specialist: true,
        // Contract stages set their own width beside the chips.
        className: spec.keeps_contracts && sitePage ? undefined : styles.specialistCol,
        cell: (row) => {
          const status = (
            <SpecialistStatus
              projectId={row.project_id}
              agent={spec.agent}
              label={spec.label}
              chips={rowSpecialists(row)[spec.agent]}
            />
          );
          return spec.keeps_contracts && sitePage ? (
            <span className={styles.stack}>
              <span className={styles.contractsStatus}>{status}</span>
              <ContractStages row={row} definitions={definitions} />
            </span>
          ) : (
            status
          );
        },
      }),
    ),
    {
      header: "Most urgent blockers",
      className: styles.list,
      cell: (row) => {
        const summary = row.summary?.summary;
        if (!summary || summary.blockers.length === 0)
          return (
            <span className={styles.muted}>
              {summary ? "none" : "not judged"}
            </span>
          );
        if (byReference(summary))
          return (
            <Collapsible
              texts={summary.blockers.map((id) => recordLine(row, id))}
              open
            >
              {summary.blockers.map((id) => (
                <RecordItem key={id} row={row} id={id} blocker />
              ))}
            </Collapsible>
          );
        return (
          <Collapsible
            texts={summary.blockers.map((b) => b.text)}
            {...listState(row.project_id)}
          >
            {summary.blockers.map((b) => (
              <li key={b.id}>
                {b.text}
                <span className={styles.small}>
                  {" "}
                  open {daysSince(b.since)} days
                  {b.waiting_on ? ` · waiting on ${b.waiting_on}` : ""}
                </span>
                <Linked item={b} row={row} />
                {(b.clears_when || b.owner) && (
                  <InfoToggle label="What clears this blocker">
                    {b.clears_when && (
                      <span className={styles.infoLine}>
                        Clears when: {b.clears_when}
                      </span>
                    )}
                    {b.owner && (
                      <span className={styles.infoLine}>Owner: {b.owner}</span>
                    )}
                  </InfoToggle>
                )}
              </li>
            ))}
          </Collapsible>
        );
      },
    },
    {
      header: "Next actions",
      className: styles.list,
      cell: (row) => {
        const summary = row.summary?.summary;
        if (summary && byReference(summary))
          return (
            <span className={styles.stack}>
              {summary.next_steps.length === 0 ? (
                <span className={styles.muted}>none</span>
              ) : (
                <Collapsible
                  texts={summary.next_steps.map((id) => recordLine(row, id))}
                  open
                >
                  {summary.next_steps.map((id) => (
                    <RecordItem key={id} row={row} id={id} />
                  ))}
                </Collapsible>
              )}
            </span>
          );
        if (!summary || summary.next_actions.length === 0)
          return (
            <span className={styles.muted}>
              {summary ? "none" : "not judged"}
            </span>
          );
        return (
          <Collapsible
            texts={summary.next_actions.map((n) => n.text)}
            {...listState(row.project_id)}
          >
            {summary.next_actions.map((n, i) => (
              <li key={i}>
                {n.text}
                <span className={styles.small}>
                  {" "}
                  {[
                    n.owner,
                    n.by
                      ? `by ${formatDay(n.by)}`
                      : n.target && `our target ${formatDay(n.target)}`,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
                <Linked item={n} row={row} />
              </li>
            ))}
          </Collapsible>
        );
      },
    },
  ];

  const frozen = columns.filter((c) => c.frozen);
  const lastFrozen = frozen[frozen.length - 1];
  const cellClass = (c: Column) =>
    [
      c.className,
      c.frozen && styles.frozen,
      c === lastFrozen && styles.frozenLast,
    ]
      .filter(Boolean)
      .join(" ");

  const pastFirstLine = columns.filter((c) => c.specialist)[SPECIALISTS_ON_FIRST_LINE];
  const splitAt = columns.findIndex((c) =>
    pastFirstLine ? c === pastFirstLine : c.header === "Most urgent blockers",
  );
  const groups = split
    ? [columns.slice(0, splitAt), columns.slice(splitAt)]
    : [columns];
  // Split, the first line's columns sit at their content's width and a filler takes the
  // rest, so each specialist follows the last rather than being pushed to the far edge.
  const fitted = (g: number) => split && g === 0;
  return (
    <div className={styles.scroll}>
      {groups.map((group, g) => (
        <table key={g} className={styles.table}>
          <thead>
            <tr>
              {group.map((c) => (
                <th
                  key={c.header}
                  className={`${styles.th} ${cellClass(c)} ${fitted(g) ? styles.fit : ""}`}
                >
                  {c.header}
                </th>
              ))}
              {fitted(g) && <th className={styles.th} aria-hidden />}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => {
              const after = g === groups.length - 1 ? afterRow?.(row) : null;
              return (
                <Fragment key={row.project_id}>
                  <tr
                    className={`${styles.row} ${
                      draggingId === row.project_id
                        ? styles.dragging
                        : draggingId
                          ? styles.shifting
                          : ""
                    }`}
                    style={rowStyle(i)}
                    {...rowAttrs?.(row)}
                  >
                    {group.map((c) => (
                      <td
                        key={c.header}
                        className={`${styles.td} ${cellClass(c)} ${fitted(g) ? styles.fit : ""}`}
                      >
                        {c.cell(row)}
                      </td>
                    ))}
                    {fitted(g) && <td className={styles.td} aria-hidden />}
                  </tr>
                  {after && (
                    <tr>
                      <td className={styles.afterRow} colSpan={group.length}>
                        {after}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      ))}
    </div>
  );
};
